import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import styled from 'styled-components';
import { FileText, ShieldCheck } from 'lucide-react';
import dayjs from '../../lib/day';

export const PAYMENT_PLAN_AUTHORIZATION_VERSION = '2026-06-03-payment-plan-authorization-v1';

export const PAYMENT_PLAN_AUTHORIZATION_ACKNOWLEDGMENT = {
  key: 'authorizationAccepted',
  label: 'I authorize this payment plan',
  body: 'I have reviewed the payment schedule, amount terms, cancellation policy, and off-session charge authorization, and I authorize Veritas Ratings to save my billing method through Stripe and charge it automatically under this plan.',
};

export const PAYMENT_PLAN_AUTHORIZATION_ACKNOWLEDGMENTS = [
  PAYMENT_PLAN_AUTHORIZATION_ACKNOWLEDGMENT,
];

const TERMS_SCROLL_THRESHOLD_PX = 8;

export const createPaymentPlanAuthorizationState = (initial = {}) => (
  PAYMENT_PLAN_AUTHORIZATION_ACKNOWLEDGMENTS.reduce((state, item) => ({
    ...state,
    [item.key]: Boolean(initial[item.key]),
  }), {})
);

export const getPaymentPlanAuthorizationSignerName = (user = {}) => (
  [user?.userFirstName, user?.userLastName].filter(Boolean).join(' ').trim()
);

export const isPaymentPlanAuthorizationComplete = (
  acknowledgments = {},
  typedSignature = '',
) => (
  PAYMENT_PLAN_AUTHORIZATION_ACKNOWLEDGMENTS.every((item) => acknowledgments[item.key]) &&
  String(typedSignature || '').trim().length >= 2
);

const moneyToCents = (value) => Math.round(Number(value || 0) * 100);

const normalizeInstallment = (installment, fallbackSequence) => ({
  sequence: Number.isFinite(Number(installment?.sequence))
    ? Number(installment.sequence)
    : fallbackSequence,
  dueDate: installment?.dueDate || null,
  amountCents: Number.isFinite(Number(installment?.amountCents))
    ? Number(installment.amountCents)
    : moneyToCents(installment?.amount),
});

export const buildPaymentPlanAuthorizationScheduleSnapshot = (source = {}) => {
  const totalCents = Number.isFinite(Number(source?.totalCents))
    ? Number(source.totalCents)
    : moneyToCents(source?.totalAmount);
  const downPaymentCents = Number.isFinite(Number(source?.downPaymentCents))
    ? Number(source.downPaymentCents)
    : moneyToCents(source?.downPaymentAmount);
  const remainingCents = Number.isFinite(Number(source?.remainingCents))
    ? Number(source.remainingCents)
    : Math.max(0, totalCents - downPaymentCents);

  return {
    totalCents,
    downPaymentCents,
    remainingCents,
    termMonths: Number(source?.termMonths || 0),
    installments: (source?.installments || []).map(normalizeInstallment),
  };
};

export const buildPaymentPlanAuthorizationPayload = ({
  acknowledgments,
  typedSignature,
  schedule,
  plan,
  supportContact,
}) => ({
  accepted: true,
  termsVersion: PAYMENT_PLAN_AUTHORIZATION_VERSION,
  termsReviewed: true,
  acceptanceMethod: 'single_checkbox_after_terms_scroll',
  acknowledgments: createPaymentPlanAuthorizationState(acknowledgments),
  typedSignature: String(typedSignature || '').trim(),
  acceptedAtClient: new Date().toISOString(),
  displayedSchedule: buildPaymentPlanAuthorizationScheduleSnapshot(schedule || plan || {}),
  cancellationContact: {
    email: supportContact?.email || 'billing@veritasratings.com',
    phone: supportContact?.phone || '(615) 263-4307',
  },
});

const TermsCard = styled.div`
  border: 1px solid #dbe4f0;
  border-radius: 12px;
  background: #f8fafc;
  padding: 0.95rem;
  margin-top: 1rem;

  .authorization-header {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
    color: #0f1c33;
    margin-bottom: 0.65rem;
  }

  .authorization-title {
    margin: 0;
    font-size: 0.94rem;
    line-height: 1.25;
    font-weight: 750;
  }

  .authorization-subtitle {
    margin: 0.12rem 0 0;
    color: #64748b;
    font-size: 0.82rem;
    line-height: 1.45;
  }

  .terms-scroll {
    max-height: 190px;
    overflow: auto;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background: #ffffff;
    padding: 0.8rem;
    color: #334155;
    font-size: 0.82rem;
    line-height: 1.45;
  }

  .terms-scroll h5 {
    margin: 0.75rem 0 0.2rem;
    color: #0f1c33;
    font-size: 0.78rem;
    line-height: 1.25;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-weight: 750;
  }

  .terms-scroll h5:first-child {
    margin-top: 0;
  }

  .terms-scroll p {
    margin: 0;
  }

  .ack-list {
    display: grid;
    gap: 0.35rem;
    margin-top: 0.8rem;
  }

  .ack-item {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 0.55rem;
    align-items: flex-start;
    color: #0f1c33;
    font-size: 0.84rem;
    line-height: 1.35;
  }

  .ack-item.is-disabled {
    color: #94a3b8;
    cursor: not-allowed;
  }

  .ack-item.is-disabled .ack-title,
  .ack-item.is-disabled .ack-body {
    color: #94a3b8;
  }

  .ack-item.is-disabled .form-check-input {
    background-color: #e2e8f0;
    border-color: #cbd5e1;
    box-shadow: none;
    cursor: not-allowed;
    opacity: 0.65;
  }

  .ack-item.is-disabled .form-check-input:focus {
    border-color: #cbd5e1;
    box-shadow: none;
  }

  .ack-title {
    font-weight: 700;
  }

  .ack-body {
    display: block;
    color: #64748b;
    font-size: 0.78rem;
    margin-top: 0.08rem;
  }

  .ack-disabled-note {
    color: #334155;
    font-size: 0.8rem;
    font-weight: 650;
    letter-spacing: 0.01em;
    line-height: 1.35;
    margin-bottom: 0.1rem;
  }

  .ack-disabled-note.is-hidden {
    visibility: hidden;
  }

  .signature-row {
    margin-top: 0.85rem;
  }

  .signature-row label {
    color: #0f1c33;
    font-size: 0.8rem;
    font-weight: 700;
  }

  .record-note {
    display: flex;
    gap: 0.45rem;
    align-items: flex-start;
    margin-top: 0.65rem;
    color: #64748b;
    font-size: 0.77rem;
    line-height: 1.4;
  }
`;

const formatCents = (cents) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
}).format((Number(cents) || 0) / 100);

const formatDate = (date) => (
  date && dayjs(date).isValid() ? dayjs(date).format('MMM D, YYYY') : 'the listed due date'
);

export const buildPaymentPlanAuthorizationTermsSections = ({
  schedule,
  plan,
  supportContact,
} = {}) => {
  const snapshot = buildPaymentPlanAuthorizationScheduleSnapshot(schedule || plan || {});
  const firstInstallment = snapshot.installments.find((item) => Number(item.sequence) > 0) ||
    snapshot.installments[0];
  const supportEmail = supportContact?.email || 'billing@veritasratings.com';
  const futureInstallmentCount = snapshot.termMonths || snapshot.installments.length;

  return [
    {
      title: 'Payment authorization',
      body: 'By signing below, I authorize Veritas Ratings to initiate recurring charges to my selected billing method on file through Stripe according to the payment plan I have agreed to.',
    },
    {
      title: 'Payment schedule',
      body: `Veritas Ratings is authorized to charge the agreed down payment of ${formatCents(snapshot.downPaymentCents)} during Stripe checkout and then charge future installments beginning ${formatDate(firstInstallment?.dueDate)}. The total authorized amount is ${formatCents(snapshot.totalCents)} across ${futureInstallmentCount} future installment${futureInstallmentCount === 1 ? '' : 's'}.`,
    },
    {
      title: 'How amounts are determined',
      body: 'Each installment amount is fixed at enrollment. Any adjustment to the payment amount or schedule requires prior consent before automatic charging continues under the revised plan.',
    },
    {
      title: 'Off-session payments',
      body: 'I understand that scheduled charges are initiated without my presence at the time of each transaction. I confirm that I am authorized to use the billing method provided.',
    },
    {
      title: 'Cancellation policy',
      body: `I may cancel this payment plan by emailing ${supportEmail} at least 7 business days before the next scheduled payment date. Cancellation does not relieve me of amounts already due or past due. Payments made before cancellation are non-refundable unless otherwise agreed in writing.`,
    },
    {
      title: 'Failed payments',
      body: 'If a scheduled charge fails, Veritas Ratings may retry the charge up to 3 times within a 7-day period. I agree to maintain sufficient funds or credit availability on the selected billing method.',
    },
    {
      title: 'Record of agreement',
      body: 'A record of this authorization, including my typed signature, account email, IP address, timestamp, terms version, and payment schedule, will be stored and may be used to evidence my consent for off-session payments.',
    },
  ];
};

const PaymentPlanAuthorizationTerms = ({
  acknowledgments,
  onAcknowledgmentChange,
  typedSignature,
  onTypedSignatureChange,
  schedule,
  plan,
  signerName,
  signerEmail,
  supportContact,
  disabled = false,
}) => {
  const snapshot = buildPaymentPlanAuthorizationScheduleSnapshot(schedule || plan || {});
  const termsSections = buildPaymentPlanAuthorizationTermsSections({
    schedule,
    plan,
    supportContact,
  });
  const termsScrollRef = useRef(null);
  const onAcknowledgmentChangeRef = useRef(onAcknowledgmentChange);
  const [hasReviewedTerms, setHasReviewedTerms] = useState(false);
  const acceptanceLocked = disabled || !hasReviewedTerms;
  const termsReviewKey = useMemo(() => JSON.stringify({
    totalCents: snapshot.totalCents,
    downPaymentCents: snapshot.downPaymentCents,
    remainingCents: snapshot.remainingCents,
    termMonths: snapshot.termMonths,
    installments: snapshot.installments.map((installment) => ({
      sequence: installment.sequence,
      dueDate: installment.dueDate,
      amountCents: installment.amountCents,
    })),
  }), [
    snapshot.totalCents,
    snapshot.downPaymentCents,
    snapshot.remainingCents,
    snapshot.termMonths,
    snapshot.installments,
  ]);

  useEffect(() => {
    onAcknowledgmentChangeRef.current = onAcknowledgmentChange;
  }, [onAcknowledgmentChange]);

  useEffect(() => {
    setHasReviewedTerms(false);
    onAcknowledgmentChangeRef.current?.(PAYMENT_PLAN_AUTHORIZATION_ACKNOWLEDGMENT.key, false);

    if (typeof window === 'undefined' || !window.requestAnimationFrame) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      const node = termsScrollRef.current;
      if (!node) return;
      if (node.scrollHeight <= node.clientHeight + TERMS_SCROLL_THRESHOLD_PX) {
        setHasReviewedTerms(true);
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [termsReviewKey]);

  const handleTermsScroll = () => {
    const node = termsScrollRef.current;
    if (!node || hasReviewedTerms) return;
    const scrolledToBottom =
      node.scrollTop + node.clientHeight >= node.scrollHeight - TERMS_SCROLL_THRESHOLD_PX;
    if (scrolledToBottom) {
      setHasReviewedTerms(true);
    }
  };

  return (
    <TermsCard>
      <div className="authorization-header">
        <FileText size={17} />
        <div>
          <p className="authorization-title">Payment plan authorization</p>
          <p className="authorization-subtitle">
            Review and accept these terms before Veritas Ratings saves a billing method
            and schedules automatic charges.
          </p>
        </div>
      </div>

      <div
        ref={termsScrollRef}
        className="terms-scroll"
        onScroll={handleTermsScroll}
      >
        {termsSections.map((section) => (
          <div key={section.title}>
            <h5>{section.title}</h5>
            <p>{section.body}</p>
          </div>
        ))}
      </div>

      <div className="ack-list">
        <div
          className={`ack-disabled-note${hasReviewedTerms ? ' is-hidden' : ''}`}
          aria-hidden={hasReviewedTerms}
        >
          Scroll through the authorization terms to enable acceptance.
        </div>
        {PAYMENT_PLAN_AUTHORIZATION_ACKNOWLEDGMENTS.map((item) => (
          <label
            className={`ack-item${acceptanceLocked ? ' is-disabled' : ''}`}
            key={item.key}
          >
            <Form.Check
              type="checkbox"
              checked={Boolean(acknowledgments?.[item.key])}
              disabled={acceptanceLocked}
              onChange={(event) => {
                if (!hasReviewedTerms) return;
                onAcknowledgmentChange?.(item.key, event.target.checked);
              }}
              aria-label={item.label}
            />
            <span>
              <span className="ack-title">{item.label}</span>
              <span className="ack-body">{item.body}</span>
            </span>
          </label>
        ))}
      </div>

      <Form.Group className="signature-row">
        <Form.Label>Typed electronic signature</Form.Label>
        <Form.Control
          type="text"
          value={typedSignature}
          disabled={disabled}
          placeholder={signerName || 'Enter your full name'}
          onChange={(event) => onTypedSignatureChange?.(event.target.value)}
        />
      </Form.Group>

      <div className="record-note">
        <ShieldCheck size={14} />
        <span>
          This acceptance will be recorded for
          {' '}
          {signerEmail || 'your account'}
          {' '}
          before Stripe collects or saves the billing method.
        </span>
      </div>
    </TermsCard>
  );
};

export default PaymentPlanAuthorizationTerms;
